"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { apiFetch } from "@/lib/api-fetch";

/**
 * 流程体检面板（常驻，可折叠）。
 *
 * 把「agent 流程审查」的结论变成**用户随时可见**的东西，而不是只存在于一次性报告里：
 *  · 四套并存的过程词表各有多少条（promptKey / skill 阶段键 / agent 类别 / 推进步）
 *  · skill 键错配的修复情况（exact / alias / none）
 *  · 仍然存在的缺口（哪些 promptKey 没有 skill 配置、哪些 agent 类别没有分派点）
 *
 * 数据来自只读接口 `/api/pipeline/flow-map`（它在服务端用**真实模块**计算，不是静态表）。
 */
interface FlowMap {
  counts: { promptKeys: number; skillStageKeys: number; agentCategories: number; flowSteps: number };
  skillResolution: { promptKey: string; resolvedVia: string; stageKey: string | null; skillIds: string[]; gap: boolean }[];
  gaps: {
    promptKeysWithoutSkills: string[];
    skillStageKeysUnreachable: string[];
    agentCategoriesWithoutDispatch: string[];
  };
  aliases: Record<string, string>;
  agentDispatch: Record<string, { dispatched: boolean; evidence: string }>;
}

export function FlowHealthPanel() {
  const t = useTranslations("pipeline");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<FlowMap | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await apiFetch("/api/pipeline/flow-map");
      if (!res.ok) throw new Error(`${res.status}`);
      setData((await res.json()) as FlowMap);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (open && !data) void load();
  }, [open, data, load]);

  const via = (v: string) => data?.skillResolution.filter((r) => r.resolvedVia === v).length ?? 0;

  return (
    <div className="rounded-xl border border-[var(--border-subtle)] bg-white p-3">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-xs font-medium text-[var(--text-secondary)]"
        onClick={() => setOpen(!open)}
      >
        <span>{t("flowHealth")}</span>
        {data ? (
          <span className="font-normal text-[var(--text-muted)]">
            {t("vocabCounts")
              .replace("{p}", String(data.counts.promptKeys))
              .replace("{s}", String(data.counts.skillStageKeys))
              .replace("{a}", String(data.counts.agentCategories))}
          </span>
        ) : null}
        <span className="ml-auto text-[var(--primary)]">{open ? t("cancel") : t("expand")}</span>
      </button>

      {open ? (
        <div className="mt-2 space-y-2 text-[11px] text-[var(--text-secondary)]">
          {err ? (
            <div className="text-[var(--destructive)]">
              {t("loadFailed")}: {err}{" "}
              <button type="button" className="text-[var(--primary)] underline" onClick={() => void load()}>
                {t("retry")}
              </button>
            </div>
          ) : null}
          {!data && !err ? <div className="text-[var(--text-muted)]">{t("loading")}</div> : null}
          {data ? (
            <>
              <div className="flex flex-wrap gap-3">
                <span>promptKey: {data.counts.promptKeys}</span>
                <span>skill 阶段键: {data.counts.skillStageKeys}</span>
                <span>agent 类别: {data.counts.agentCategories}</span>
                <span>推进步: {data.counts.flowSteps}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="text-[var(--success)]">精确命中 {via("exact")}</span>
                <span className="text-[var(--success)]">别名命中 {via("alias")}</span>
                <span className={via("none") > 0 ? "text-[var(--warning)]" : "text-[var(--text-muted)]"}>
                  未命中 {via("none")}
                </span>
                <span className={data.gaps.skillStageKeysUnreachable.length ? "text-[var(--warning)]" : "text-[var(--text-muted)]"}>
                  不可达 skill 条目 {data.gaps.skillStageKeysUnreachable.length}
                </span>
                <span
                  className={
                    data.gaps.agentCategoriesWithoutDispatch.length ? "text-[var(--destructive)]" : "text-[var(--text-muted)]"
                  }
                >
                  无分派 agent 类别 {data.gaps.agentCategoriesWithoutDispatch.length}
                </span>
              </div>
              {Object.keys(data.aliases).length ? (
                <div className="text-[var(--text-muted)]">
                  别名：{Object.entries(data.aliases).map(([k, v]) => `${k}→${v}`).join("、")}
                </div>
              ) : null}
              {data.gaps.promptKeysWithoutSkills.length ? (
                <div className="rounded bg-[var(--warning)]/5 p-2 text-[var(--warning)]">
                  仍无 skill 配置的 promptKey（属内容决策，未擅自替用户决定）：{" "}
                  {data.gaps.promptKeysWithoutSkills.join("、")}
                </div>
              ) : null}
              <div className="text-[var(--text-muted)]">
                agent 分派点：{Object.entries(data.agentDispatch).map(([k, v]) => `${k}@${v.evidence.split(":")[1] ?? "?"}`).join("、")}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
